<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'AboutModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const apiVersion = ref(null);
  const versions = ref(null);
  const currentYear = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    headerClass: 'bg-light',
    noFooter: true,
    size: 'md',
  }));

  const $data = computed(() => ({
    apiVersion: apiVersion.value,
    versions: versions.value,
    currentYear: currentYear.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_apiVersion = async () => {
    return $fn.value.fetch({ url: '/version' }).then((resp) => resp.data);
  };
  const $$load_versions = async () => {
    return [
      { name: 'Frontend', version: $app.value.PACKAGE_VERSION },
      { name: 'API', version: apiVersion.value },
      { name: 'Docs', version: 'not installed' },
      { name: 'Auth / Keycloak', version: '25.0.0' },
    ];
  };
  const $$load_currentYear = async () => {
    return new Date().getUTCFullYear();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'apiVersion' },
      { name: 'versions', dependsOn: ['apiVersion'] },
      { name: 'currentYear' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      apiVersion: $$load_apiVersion,
      versions: $$load_versions,
      currentYear: $$load_currentYear,
    },
    ux: {},
    dataRefs: {
      apiVersion,
      versions,
      currentYear,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('About Integro') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_7u38oczga_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_7u38oczga =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_7u38oczga_getActionArgs,
    });
  const inst_evh_7315092382398562_close_73mhnxgf2 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_7u38oczga,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_73mhnxgf2],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_4shmkcs5f =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8gllhjx73 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_4shmkcs5f,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8gllhjx73],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_5c3t5bxkl =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_p59pskrx2 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_5c3t5bxkl,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_p59pskrx2],
    event: 'close',
  });

  const actions = {
    inst_evh_7315092382398562_close_73mhnxgf2,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_8gllhjx73,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_p59pskrx2,
    inst_evh_8564662037462133_close,
  };

  const evh_7315092382398562_close_73mhnxgf2 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_73mhnxgf2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_8gllhjx73 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8gllhjx73.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_p59pskrx2 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_p59pskrx2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="AboutModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <h4>Integro v{{ $app.PRODUCT_VERSION }}</h4>
      <div class="mt-3">
        Copyright © 2006-{{ currentYear }} EuroSand Computer Kft.
      </div>
      <div>License type: Commercial</div>
      <table class="table table-sm table-striped table-borderless mt-3">
        <thead>
          <tr>
            <th>{{ gettext('Component') }}</th>
            <th>{{ gettext('Version') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="v in versions" :key="v.name">
            <td>{{ v.name }}</td>
            <td>{{ v.version }}</td>
          </tr>
        </tbody>
      </table>
    </UcBody>
  </ItpPage>
</template>
